import React, { useState, useCallback, useRef } from 'react';
import { FiX, FiUpload, FiFile, FiEdit3, FiCheck, FiRotateCw, FiAlertCircle, FiImage } from 'react-icons/fi';
import { MdUploadFile, MdOutlineDriveFolderUpload } from 'react-icons/md';
import { FaFileCsv, FaFilePdf, FaFileExcel } from 'react-icons/fa6';
import { VscJson } from 'react-icons/vsc';

type UploadStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled';

export interface UploadFile {
  id: string;
  file: File;
  name: string;
  originalName: string;
  size: number;
  status: UploadStatus;
  progress: number;
  error?: string;
  isRenaming?: boolean;
}

interface FileUploadManagerProps {
  currentPath: string;
  onUploadComplete?: (files: UploadFile[]) => void;
  onClose: (files?: UploadFile[]) => void;
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'csv':
      return <FaFileCsv className="text-green-500" size={20} />;
    case 'pdf':
      return <FaFilePdf className="text-red-500" size={20} />;
    case 'json':
      return <VscJson className="text-yellow-500" size={20} />;
    case 'xlsx':
    case 'xls':
      return <FaFileExcel className="text-green-600" size={20} />;
    case 'jpeg':
    case 'jpg':
    case 'png':
    case 'gif':
      return <FiImage className="text-cyan-400" size={20} />;
    default:
      return <FiFile className="text-gray-400" size={20} />;
  }
};

const FileUploadManager: React.FC<FileUploadManagerProps> = ({
  currentPath,
  onClose,
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const uploadTimeoutsRef = useRef<Map<string, XMLHttpRequest>>(new Map());

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const MAX_FILE_SIZE = 50 * 1024 * 1024 * 1024; // 50 GB in bytes

    const newFiles: UploadFile[] = selectedFiles.map(file => {
      // Check if file exceeds 50 GB limit
      if (file.size > MAX_FILE_SIZE) {
        return {
          id: generateId(),
          file,
          name: file.name,
          originalName: file.name,
          size: file.size,
          status: 'error' as UploadStatus,
          progress: 0,
          error: 'File exceeds 50 GB limit',
        };
      }

      return {
        id: generateId(),
        file,
        name: file.name,
        originalName: file.name,
        size: file.size,
        status: 'queued' as UploadStatus,
        progress: 0,
      };
    });

    setFiles(prev => [...prev, ...newFiles]);

    // Reset input
    if (e.target) {
      e.target.value = '';
    }
  }, []);

  const handleRemoveFile = useCallback((id: string) => {
    // Cancel upload if in progress
    const xhr = uploadTimeoutsRef.current.get(id);
    if (xhr && xhr.abort) {
      xhr.abort();
      uploadTimeoutsRef.current.delete(id);
    }

    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const handleRenameStart = useCallback((id: string) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, isRenaming: true } : f
    ));
  }, []);

  const handleRenameEnd = useCallback((id: string, newName: string) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, name: newName, isRenaming: false } : f
    ));
  }, []);

  const handleRenameCancel = useCallback((id: string) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, isRenaming: false } : f
    ));
  }, []);

  const uploadToAzure = useCallback(async (file: UploadFile) => {
    // Azure SAS URL base
    const baseSasUrl = 'https://stgoceanxdata.blob.core.windows.net/science/a40b4074-df31-48fb-8897-1d9b0eaa0c04/test-for-eric';
    // SAS token with corrected sdd parameter (should be '2' not '2a')
    const sasToken = 'sp=racwl&st=2025-09-25T14:34:34Z&se=2025-11-25T23:49:34Z&spr=https&sv=2024-11-04&sr=d&sig=vsOevqAkDrsfhWK8YInZ1McGKFHidWBpNOTgtN2obKQ%3D&sdd=2';

    // Construct the full URL for this file
    // Azure expects the file name to be part of the path, not double-encoded
    const fileName = file.name.replace(/ /g, '%20'); // Manual space encoding for Azure
    const fileUrl = `${baseSasUrl}/${fileName}?${sasToken}`;

    // Determine content type
    const getContentType = (fileName: string) => {
      const ext = fileName.split('.').pop()?.toLowerCase();
      switch (ext) {
        case 'json': return 'application/json';
        case 'pdf': return 'application/pdf';
        case 'txt': return 'text/plain';
        case 'csv': return 'text/csv';
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        case 'png': return 'image/png';
        case 'gif': return 'image/gif';
        case 'mp4': return 'video/mp4';
        case 'zip': return 'application/zip';
        default: return 'application/octet-stream';
      }
    };

    try {
      // Start upload
      setFiles(prev => prev.map(f =>
        f.id === file.id
          ? { ...f, status: 'uploading', progress: 0 }
          : f
      ));

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          setFiles(prev => prev.map(f =>
            f.id === file.id
              ? { ...f, progress: Math.round(progress) }
              : f
          ));
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setFiles(prev => prev.map(f =>
            f.id === file.id
              ? { ...f, status: 'done', progress: 100 }
              : f
          ));
        } else {
          console.error(`Failed to upload ${file.name} (Status: ${xhr.status} - ${xhr.statusText})`);
          setFiles(prev => prev.map(f =>
            f.id === file.id
              ? { ...f, status: 'error', progress: 0, error: `Upload failed: HTTP ${xhr.status} - ${xhr.statusText || 'Unknown error'}` }
              : f
          ));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        setFiles(prev => prev.map(f =>
          f.id === file.id
            ? { ...f, status: 'error', progress: 0, error: 'Network error occurred' }
            : f
        ));
      });

      // Handle abort
      xhr.addEventListener('abort', () => {
        setFiles(prev => prev.map(f =>
          f.id === file.id
            ? { ...f, status: 'cancelled', progress: 0 }
            : f
        ));
      });

      // Store xhr for potential cancellation
      uploadTimeoutsRef.current.set(file.id, xhr);

      // Send the request
      xhr.open('PUT', fileUrl);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', getContentType(file.name));
      xhr.send(file.file);

    } catch (error) {
      console.error('Upload error:', error);
      setFiles(prev => prev.map(f =>
        f.id === file.id
          ? { ...f, status: 'error', progress: 0, error: 'Failed to start upload' }
          : f
      ));
    }
  }, []);

  const handleUpload = useCallback(() => {
    setIsUploading(true);
    setShowConfirmation(false);

    // Start uploading all queued files
    const queuedFiles = files.filter(f => f.status === 'queued');
    queuedFiles.forEach((file, index) => {
      // Make every 3rd upload fail (for testing)
      const shouldFail = (index + 1) % 3 === 0;

      if (shouldFail) {
        // Simulate immediate upload failure for every 3rd file
        setTimeout(() => {
          setFiles(prev => prev.map(f =>
            f.id === file.id
              ? { ...f, status: 'error' as UploadStatus, progress: 0, error: 'Upload failed: Connection timeout' }
              : f
          ));
        }, 1000); // Fail after 1 second
      } else {
        // Normal upload for other files
        uploadToAzure(file);
      }
    });
  }, [files, uploadToAzure]);

  const handleCancelUpload = useCallback((id: string) => {
    const xhr = uploadTimeoutsRef.current.get(id);
    if (xhr && xhr.abort) {
      xhr.abort(); // This will trigger the abort event listener
      uploadTimeoutsRef.current.delete(id);
    } else {
      // If no active upload, just mark as cancelled
      setFiles(prev => prev.map(f =>
        f.id === id
          ? { ...f, status: 'cancelled', progress: 0 }
          : f
      ));
    }
  }, []);

  const handleRetry = useCallback((id: string) => {
    const file = files.find(f => f.id === id);
    if (file) {
      setFiles(prev => prev.map(f =>
        f.id === id
          ? { ...f, status: 'queued', progress: 0, error: undefined }
          : f
      ));

      if (isUploading) {
        uploadToAzure({ ...file, status: 'queued', progress: 0 });
      }
    }
  }, [files, isUploading, uploadToAzure]);

  const queuedCount = files.filter(f => f.status === 'queued').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const completedCount = files.filter(f => f.status === 'done').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Upload Files</h2>
              <p className="mt-1 text-sm text-gray-500">
                Upload to: {currentPath || '/'}
              </p>
            </div>
            <button
              onClick={() => onClose()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* File Selection */}
        {!isUploading && (
          <div className="p-6 border-b border-gray-200">
            <div className="flex gap-3">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="*"
              />
              <input
                ref={folderInputRef}
                type="file"
                // @ts-ignore - webkitdirectory is not in the type definition
                webkitdirectory=""
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />

              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <MdUploadFile className="w-5 h-5" />
                Select Files
              </button>

              <button
                onClick={() => folderInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <MdOutlineDriveFolderUpload className="w-5 h-5" />
                Select Folder
              </button>
            </div>
          </div>
        )}

        {/* File List */}
        <div className="flex-1 overflow-y-auto p-6">
          {files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <FiUpload className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No files selected</p>
              <p className="text-sm mt-1">Select files or folders to upload</p>
            </div>
          ) : (
            <div className="space-y-3">
              {files.map(file => (
                <FileItem
                  key={file.id}
                  file={file}
                  onRemove={handleRemoveFile}
                  onRenameStart={handleRenameStart}
                  onRenameEnd={handleRenameEnd}
                  onRenameCancel={handleRenameCancel}
                  onCancel={handleCancelUpload}
                  onRetry={handleRetry}
                  isUploading={isUploading}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {queuedCount > 0 && <span>{queuedCount} queued</span>}
              {uploadingCount > 0 && <span className="ml-3">{uploadingCount} uploading</span>}
              {completedCount > 0 && <span className="ml-3 text-green-600">{completedCount} completed</span>}
              {errorCount > 0 && <span className="ml-3 text-red-600">{errorCount} failed</span>}
            </div>

            <div className="flex gap-3">
              {!isUploading && files.length > 0 && (
                <>
                  <button
                    onClick={() => setFiles([])}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => setShowConfirmation(true)}
                    disabled={queuedCount === 0}
                    className={`px-6 py-2 rounded-lg transition-colors ${
                      queuedCount > 0
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Upload {queuedCount} {queuedCount === 1 ? 'File' : 'Files'}
                  </button>
                </>
              )}

              {isUploading && uploadingCount === 0 && (
                <div className="relative group">
                  <button
                    onClick={() => onClose(files)}
                    disabled={errorCount > 0}
                    className={`px-6 py-2 rounded-lg transition-colors ${
                      errorCount > 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Proceed
                  </button>
                  {errorCount > 0 && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded whitespace-nowrap z-50 border bg-gray-800 text-gray-100 border-gray-600 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                      Retry or remove the failed upload(s) to proceed
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <ConfirmationDialog
            fileCount={queuedCount}
            totalSize={files.filter(f => f.status === 'queued').reduce((acc, f) => acc + f.size, 0)}
            onConfirm={handleUpload}
            onCancel={() => setShowConfirmation(false)}
          />
        )}
      </div>
    </div>
  );
};

interface FileItemProps {
  file: UploadFile;
  onRemove: (id: string) => void;
  onRenameStart: (id: string) => void;
  onRenameEnd: (id: string, newName: string) => void;
  onRenameCancel: (id: string) => void;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  isUploading: boolean;
}

const FileItem: React.FC<FileItemProps> = ({
  file,
  onRemove,
  onRenameStart,
  onRenameEnd,
  onRenameCancel,
  onCancel,
  onRetry,
  isUploading,
}) => {
  const [tempName, setTempName] = useState(file.name);

  const handleRenameSubmit = () => {
    if (tempName.trim() && tempName !== file.name) {
      onRenameEnd(file.id, tempName.trim());
    } else {
      onRenameCancel(file.id);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">{getFileIcon(file.name)}</div>

            {file.isRenaming ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRenameSubmit();
                    if (e.key === 'Escape') onRenameCancel(file.id);
                  }}
                  className="px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
                  autoFocus
                />
                <button
                  onClick={handleRenameSubmit}
                  className="p-1 text-green-600 hover:bg-green-100 rounded"
                >
                  <FiCheck className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onRenameCancel(file.id)}
                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{file.name}</span>
                  {!isUploading && file.status === 'queued' && (
                    <button
                      onClick={() => onRenameStart(file.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    >
                      <FiEdit3 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1">
                  <span className="text-sm text-gray-500">{formatFileSize(file.size)}</span>
                  {file.status === 'uploading' && (
                    <span className="text-sm text-blue-600">Uploading... {Math.round(file.progress)}%</span>
                  )}
                  {file.status === 'done' && (
                    <span className="text-sm text-green-600">Completed</span>
                  )}
                  {file.status === 'error' && (
                    <span className="text-sm text-red-600">{file.error}</span>
                  )}
                  {file.status === 'cancelled' && (
                    <span className="text-sm text-gray-500">Cancelled</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Progress bar */}
          {(file.status === 'uploading' || (file.status === 'error' && file.progress > 0)) && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    file.status === 'error' ? 'bg-red-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${file.progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4">
          {file.status === 'queued' && !isUploading && (
            <button
              onClick={() => onRemove(file.id)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}

          {file.status === 'uploading' && (
            <button
              onClick={() => onCancel(file.id)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}

          {(file.status === 'error' || file.status === 'cancelled') && (
            <>
              <button
                onClick={() => onRetry(file.id)}
                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                title="Retry upload"
              >
                <FiRotateCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => onRemove(file.id)}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                title="Remove file"
              >
                <FiX className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface ConfirmationDialogProps {
  fileCount: number;
  totalSize: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  fileCount,
  totalSize,
  onConfirm,
  onCancel,
}) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 rounded-lg">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
            <FiAlertCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Confirm Upload</h3>
            <p className="mt-2 text-sm text-gray-600">
              You are about to upload {fileCount} {fileCount === 1 ? 'file' : 'files'}
              ({formatFileSize(totalSize)}) to the server.
            </p>
            <p className="mt-2 text-sm text-gray-500">
              This action cannot be undone once the upload starts.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadManager;